<template>
	<div class="vcx-portfolio-tables">
		<div class="vcx-grid">
			<BasePlaceHolder
				class="vcx-col-12 vcx-col-9-md vcx-body-xl vcx-mb-400"
				:placehold="isLoading"
				theme="dark"
				:line-count="2"
			>
				<RichTextComponent :blocks="portfolioDescription" />
			</BasePlaceHolder>
		</div>
		<div class="vcx-grid">
			<div class="vcx-col-12 vcx-col-6-md vcx-col-7-lg">
				<div class="vcx-portfolio-table vcx-portfolio-table--with-header">
					<div class="vcx-portfolio-table__header-row">
						<h3 class="vcx-h4 vcx-text-inverse">Top 10 holdings</h3>
						<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="5rem">
							<span class="vcx-body-sm vcx-text-tertiary">{{ asOfLabel }}</span>
						</BasePlaceHolderInline>
					</div>
					<table class="vcx-portfolio-table__body">
						<thead class="vcx-sr-only">
							<tr>
								<th scope="col">Rank</th>
								<th scope="col">Company Name</th>
								<th scope="col">Percentage</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(holding, holdingIdx) in displayHoldings"
								:key="holdingIdx"
								class="vcx-text-inverse"
							>
								<td class="vcx-portfolio-table__rank">{{ holdingIdx + 1 }}</td>
								<td class="vcx-subheading">
									<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="8rem">{{
										holding.key
									}}</BasePlaceHolderInline>
								</td>
								<td class="vcx-portfolio-table__value vcx-heading-base">
									<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="3rem">{{
										holding.value
									}}</BasePlaceHolderInline>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="8rem">
					<BaseButton
						v-if="!isLoading && portfolioHoldings.length > 10"
						class="vcx-portfolio-tables__see-all-holdings vcx-text-brand vcx-mt-150"
						:no-style="true"
						@click="openAllHoldingsModal"
						>See all holdings</BaseButton
					>
				</BasePlaceHolderInline>
			</div>
			<div class="vcx-col-12 vcx-col-6-md vcx-col-5-lg">
				<div class="vcx-portfolio-table vcx-portfolio-table--no-border vcx-p-100 vcx-mb-150">
					<h3 class="vcx-h5 vcx-text-gray-40 vcx-mb-150">Sector breakdown</h3>
					<table class="vcx-portfolio-table__body">
						<thead class="vcx-sr-only">
							<tr>
								<th scope="col">Sector</th>
								<th scope="col">Percentage</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="sector in displaySectors" :key="sector.key" class="vcx-text-inverse">
								<td class="vcx-body-md">
									<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="10rem">{{
										sector.key
									}}</BasePlaceHolderInline>
								</td>
								<td class="vcx-portfolio-table__value vcx-body-md">
									<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="3rem">{{
										sector.value
									}}</BasePlaceHolderInline>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="vcx-portfolio-table vcx-portfolio-table--no-border vcx-p-100">
					<h3 class="vcx-h5 vcx-text-gray-40 vcx-mb-150">Private/public breakdown</h3>
					<table class="vcx-portfolio-table__body">
						<thead class="vcx-sr-only">
							<tr>
								<th scope="col">Type</th>
								<th scope="col">Percentage</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in displayBreakdown" :key="item.key" class="vcx-text-inverse">
								<td class="vcx-body-md">
									<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="6rem">{{
										item.key
									}}</BasePlaceHolderInline>
								</td>
								<td class="vcx-portfolio-table__value vcx-body-md">
									<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="3rem">{{
										item.value
									}}</BasePlaceHolderInline>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="vcx-body-sm vcx-text-right vcx-text-gray-40 text-align-right">
					<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="10rem">
						{{ disclaimer }}
					</BasePlaceHolderInline>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CustomUnixSpacetimeFormat } from 'types/spacetime-formats';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityKeyValueData } from 'types/sanity/charts-data';
import { SanityRichTextBlock } from 'types/sanity';
import spacetime from 'spacetime';
import { useAppStore } from '@stores/app';
import VcxSeeAllHoldingsModal from '@components/vcx/vcx-see-all-holdings-modal.vue';

interface Props {
	isLoading?: boolean;
	portfolioAsOfDate: string;
	portfolioDescription: Array<SanityRichTextBlock>;
	portfolioHoldings: Array<SanityKeyValueData>;
	sectorBreakdown: Array<SanityKeyValueData>;
	privatePublicBreakdown: Array<SanityKeyValueData>;
	disclaimer: string;
}

const props = withDefaults(defineProps<Props>(), {
	isLoading: false
});

const appStore = useAppStore();

const emptyRows = (count: number): Array<SanityKeyValueData> =>
	Array.from({ length: count }, () => ({ key: '', value: '' }));

const asOfLabel = computed(() => {
	if (!props.portfolioAsOfDate) return '';
	return `as of ${spacetime(props.portfolioAsOfDate, 'utc').unixFmt(CustomUnixSpacetimeFormat.UNIX_SINGLE_DIGIT_SHORT_YEAR)}`;
});

const displayHoldings = computed(() => {
	if (props.isLoading) {
		return emptyRows(10);
	}
	return props.portfolioHoldings.slice(0, 10);
});

const displaySectors = computed(() => {
	if (props.isLoading) {
		return emptyRows(8);
	}
	return props.sectorBreakdown;
});

const displayBreakdown = computed(() => {
	if (props.isLoading) {
		return emptyRows(3);
	}
	return props.privatePublicBreakdown;
});

function openAllHoldingsModal(): void {
	appStore.updateCurrentModal({
		modal: VcxSeeAllHoldingsModal,
		props: {
			asOfLabel: asOfLabel.value,
			holdings: props.portfolioHoldings
		}
	});
}
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive' as *;

.vcx-portfolio-tables {
	width: 100%;

	&__see-all-holdings {
		text-decoration: underline;
		&:hover {
			opacity: 0.8;
		}
	}
}

.vcx-portfolio-table {
	width: 100%;

	&__header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--vcx-space-150);
		background-color: rgba(255, 255, 255, 0.04);
		border: 1px solid var(--vcx-border-dark);
		border-radius: var(--vcx-radius-075) var(--vcx-radius-075) 0 0;
	}

	&--with-header &__body {
		border-top-left-radius: 0;
		border-top-right-radius: 0;
		border-top: none;
	}

	&--no-border &__body {
		border: none;
		border-radius: 0;
		overflow: visible;
	}

	&__body {
		width: 100%;
		border: 1px solid var(--vcx-border-dark);
		border-radius: var(--vcx-radius-075);
		overflow: hidden;
		border-collapse: separate;
		border-spacing: 0;

		tr {
			display: grid;
			grid-template-columns: 1fr auto;
			gap: var(--vcx-space-150);
			align-items: center;
			padding: var(--vcx-space-100) 0;
			border-bottom: 1px solid var(--vcx-border-dark-medium);
		}
	}

	&--with-header tr {
		grid-template-columns: auto 1fr auto;
		padding: var(--vcx-space-150) var(--vcx-space-200);
		font-family: var(--vcx-font-heading);

		&:last-child {
			border-bottom: none;
		}
	}
	@include vcx-media-smo {
		&--with-header tr {
			padding: var(--vcx-space-100) var(--vcx-space-150);
		}
	}

	&__rank {
		font-weight: var(--vcx-font-weight-semibold);
		min-width: 26px;
		font-family: var(--vcx-font-heading);
		font-size: var(--vcx-font-size-subheading);
	}

	&__value {
		font-weight: var(--vcx-font-weight-semibold);
		text-align: right;
		white-space: nowrap;
	}

	&--with-header {
		.vcx-portfolio-table__value {
			font-family: var(--vcx-font-heading);
			font-size: var(--vcx-font-size-subheading);
		}
	}
}
</style>
